/**
 * TikTok Pixel (ttq) bootstrap for storefronts.
 * Init + page() on load; track() helper for later conversion events.
 */

let activePixelId = null;
let scriptLoading = false;
// The pixel is initialised on idle (after first paint). Events that fire before that are kept and replayed.
let pendingPixelId = null;
let pendingEvents = [];

/** Tell the tracker a pixel WILL be initialised shortly: early events are queued instead of dropped. */
export function markTikTokPixelPending(pixelId) {
  const id = normalizeTikTokPixelId(pixelId);
  if (id && !activePixelId) pendingPixelId = id;
}

export const TIKTOK_PIXEL_CURRENCY = "ILS";

function ensureTtqStub() {
  if (typeof window === "undefined") return null;
  if (window.ttq && typeof window.ttq === "object") return window.ttq;

  const ttq = (window.ttq = window.ttq || []);
  window.TiktokAnalyticsObject = "ttq";

  ttq.methods = [
    "page",
    "track",
    "identify",
    "instances",
    "debug",
    "on",
    "off",
    "once",
    "ready",
    "alias",
    "group",
    "enableCookie",
    "disableCookie",
    "holdConsent",
    "revokeConsent",
    "grantConsent",
  ];
  ttq.setAndDefer = function (t, e) {
    t[e] = function () {
      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) {
    ttq.setAndDefer(ttq, ttq.methods[i]);
  }
  ttq.instance = function (id) {
    const e = (ttq._i = ttq._i || {})[id] || [];
    for (let n = 0; n < ttq.methods.length; n++) {
      ttq.setAndDefer(e, ttq.methods[n]);
    }
    return e;
  };
  ttq.load = function (pixelId, opts) {
    const src = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[pixelId] = [];
    ttq._i[pixelId]._u = src;
    ttq._t = ttq._t || {};
    ttq._t[pixelId] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[pixelId] = opts || {};
    if (typeof document === "undefined") return;
    if (document.getElementById("tiktok-pixel-events")) return;
    if (scriptLoading) return;
    scriptLoading = true;
    const script = document.createElement("script");
    script.id = "tiktok-pixel-events";
    script.async = true;
    script.src = `${src}?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`;
    script.onload = () => {
      scriptLoading = false;
    };
    script.onerror = () => {
      scriptLoading = false;
    };
    const first = document.getElementsByTagName("script")[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  };

  return ttq;
}

/** Alphanumeric Pixel ID, or empty string if invalid. */
export function normalizeTikTokPixelId(raw) {
  return String(raw ?? "").replace(/[^a-zA-Z0-9]/g, "");
}

/**
 * Load TikTok Pixel base code (once), load the given Pixel ID, and fire page() once.
 */
export function initTikTokPixel(pixelId) {
  if (typeof window === "undefined") return false;
  const id = normalizeTikTokPixelId(pixelId);
  if (!id) return false;

  const ttq = ensureTtqStub();
  if (!ttq) return false;

  // Already initialized for this pixel on this page — do not load/page again
  // (covers React StrictMode double-effects and settings re-renders).
  if (activePixelId === id) {
    return true;
  }

  ttq.load(id);
  activePixelId = id;
  ttq.page();
  pendingPixelId = null;
  const queued = pendingEvents;
  pendingEvents = [];
  queued.forEach(([name, params, eventId]) => trackTikTokEvent(name, params, eventId));
  return true;
}

/** Fire a TikTok browser event if the pixel is initialized. */
export function trackTikTokEvent(eventName, params = {}, eventId = null) {
  if (typeof window === "undefined" || !eventName) return;
  if (!activePixelId) {
    if (pendingPixelId && pendingEvents.length < 20) pendingEvents.push([eventName, params, eventId]);
    return;
  }
  if (!window.ttq) return;
  const options = eventId ? { event_id: String(eventId) } : undefined;
  if (typeof window.ttq.track !== "function" && !Array.isArray(window.ttq)) return;
  if (options) {
    window.ttq.track(eventName, params || {}, options);
  } else {
    window.ttq.track(eventName, params || {});
  }
}

function roundMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

/** ViewContent params from a catalog product object. */
export function buildTikTokViewContentParams(product) {
  const id = String(product?.id || product?.product_id || "").trim();
  const value = roundMoney(product?.price ?? product?.base_price ?? 0);
  return {
    content_id: id || undefined,
    content_type: "product",
    value,
    currency: TIKTOK_PIXEL_CURRENCY,
  };
}

/**
 * AddToCart params from a cart line (regular / custom / bundle).
 * @param {object} item
 * @param {(item: object) => number} [unitPriceFn]
 */
export function buildTikTokAddToCartParams(item, unitPriceFn) {
  const id = String(item?.id || item?.product_id || "").trim();
  const qty = Math.max(1, Number(item?.quantity) || 1);
  const unit =
    typeof unitPriceFn === "function"
      ? roundMoney(unitPriceFn(item))
      : roundMoney(item?.price ?? item?.baseUnitPrice ?? 0);
  return {
    content_id: id || undefined,
    content_type: "product",
    value: roundMoney(unit * qty),
    currency: TIKTOK_PIXEL_CURRENCY,
    quantity: qty,
  };
}

/**
 * InitiateCheckout params from the full cart.
 * @param {object[]} cart
 * @param {(item: object) => number} unitPriceFn
 */
export function buildTikTokInitiateCheckoutParams(cart, unitPriceFn) {
  const lines = Array.isArray(cart) ? cart : [];
  const contents = [];
  let value = 0;
  for (const item of lines) {
    const id = String(item?.id || item?.product_id || "").trim();
    const qty = Math.max(1, Number(item?.quantity) || 1);
    const unit =
      typeof unitPriceFn === "function"
        ? roundMoney(unitPriceFn(item))
        : roundMoney(item?.price ?? item?.baseUnitPrice ?? 0);
    value += unit * qty;
    if (id) {
      contents.push({
        content_id: id,
        content_type: "product",
        quantity: qty,
        price: unit,
      });
    }
  }
  return {
    value: roundMoney(value),
    currency: TIKTOK_PIXEL_CURRENCY,
    contents,
  };
}

export function trackTikTokViewContent(product) {
  trackTikTokEvent("ViewContent", buildTikTokViewContentParams(product));
}

export function trackTikTokAddToCart(item, unitPriceFn) {
  trackTikTokEvent("AddToCart", buildTikTokAddToCartParams(item, unitPriceFn));
}

export function trackTikTokInitiateCheckout(cart, unitPriceFn) {
  trackTikTokEvent("InitiateCheckout", buildTikTokInitiateCheckoutParams(cart, unitPriceFn));
}
